import React, { useState, useEffect } from 'react';

/**
 * Animated Maestra logo reveal using the ORIGINAL brand logo.
 * 
 * Phase 1: Logo image fades in softly (0 → 0.8s)
 * Phase 2: "MAESTRA" wordmark reveals left-to-right (0.6s → 1.4s)
 * 
 * Props:
 *   size: 'sm' | 'md' | 'lg'
 *   onComplete: callback when animation finishes
 */
const MaestraLogo = ({ size = 'md', onComplete }) => {
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    const t1 = setTimeout(() => setPhase(1), 50);
    const t2 = setTimeout(() => setPhase(2), 600);
    const t3 = setTimeout(() => {
      setPhase(3);
      onComplete?.();
    }, 1500);

    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [onComplete]);

  const logoSizes = { sm: '66px', md: '90px', lg: '220px' };
  const wordmarkSizes = { sm: '8px', md: '11px', lg: '16px' };
  // Negative margin compensates for the transparent padding baked into the logo PNG.
  // Must scale with logo size so the wordmark doesn't overlap the glyph on smaller variants.
  const wordmarkOffsets = { sm: '-12px', md: '-18px', lg: '-44px' };
  const logoH = logoSizes[size] || logoSizes.md;
  const wordmarkFs = wordmarkSizes[size] || wordmarkSizes.md;
  const wordmarkOffset = wordmarkOffsets[size] || wordmarkOffsets.md;

  return (
    <div
      data-testid="maestra-logo-animated"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '2px',
      }}
    >
      {/* Original logo image */}
      <img
        src="/maestra-logo.png"
        alt="Maestra"
        data-testid="maestra-logo-img"
        style={{
          height: logoH,
          width: 'auto',
          objectFit: 'contain',
          opacity: phase >= 1 ? 1 : 0,
          transform: phase >= 1 ? 'scale(1)' : 'scale(0.96)',
          transition: 'opacity 0.7s cubic-bezier(0.4, 0, 0.2, 1), transform 0.7s cubic-bezier(0.4, 0, 0.2, 1), filter 0.9s cubic-bezier(0.4, 0, 0.2, 1)',
          filter: phase >= 1
            ? 'drop-shadow(0 18px 28px rgba(60, 40, 20, 0.14)) drop-shadow(0 6px 10px rgba(60, 40, 20, 0.08))'
            : 'drop-shadow(0 0 0 rgba(0,0,0,0))',
        }}
      />

      {/* Wordmark */}
      <div style={{ overflow: 'hidden', marginTop: wordmarkOffset }}>
        <div
          data-testid="maestra-wordmark"
          style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: wordmarkFs,
            letterSpacing: '0.35em',
            textTransform: 'uppercase',
            color: '#B89968',
            clipPath: phase >= 2 ? 'inset(0 0% 0 0)' : 'inset(0 100% 0 0)',
            transition: 'clip-path 0.8s cubic-bezier(0.25, 0.1, 0.25, 1)',
          }}
        >
          MAESTRA
        </div>
      </div>
    </div>
  );
};

export default MaestraLogo;
